import { useRef, useEffect } from "react";
import { FilterItemPlusIcon } from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/icons/filter-item-plus-icon";
import {
  LiveIntegrationColumn,
  LiveIntegrationFilter,
} from "frontend/canvas-designer-new/elements-toolbar/widgets/live-integration/utils";
import classNames from "classnames";
import FilterItem from "./filter-item";
import { EMPTY_DEFAULT_COLUMN, SUPPORTED_COLUMN_TYPES } from "./types";
import styles from "./synced-card-stack-filters.module.css";
import tracking from "frontend/tracking";

interface SyncedCardStackFiltersProps {
  columns: LiveIntegrationColumn[];
  filters: LiveIntegrationFilter[];
  onFiltersChange: (filters: LiveIntegrationFilter[]) => void;
}

export default function SyncedCardStackFilters({ columns, filters, onFiltersChange }: SyncedCardStackFiltersProps) {
  const supportedColumns = columns?.filter((column) => SUPPORTED_COLUMN_TYPES.includes(column.type)) ?? [];
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const setScrollableFadeEffect = (element: HTMLDivElement) => {
    const isScrollable = element.scrollHeight > element.clientHeight;
    if (!isScrollable) {
      element.classList.remove(styles.isBottomOverflowing);
      return;
    }

    const isScrolledToBottom = element.scrollHeight < element.clientHeight + element.scrollTop + 1;
    element.classList.toggle(styles.isBottomOverflowing, !isScrolledToBottom);
  };

  const handleRef = (element: HTMLDivElement | null) => {
    scrollContainerRef.current = element;
    if (element) {
      setScrollableFadeEffect(element);
    }
  };

  useEffect(() => {
    if (scrollContainerRef.current) {
      setScrollableFadeEffect(scrollContainerRef.current);
    }
  }, [filters]);

  function onRemoveFilter(index: number) {
    const newFilters = filters.filter((_, filterIndex) => filterIndex !== index);
    onFiltersChange(newFilters);
  }

  function onFilterChange(index: number, filter: LiveIntegrationFilter) {
    const newFilters = structuredClone(filters);
    newFilters[index] = filter;
    onFiltersChange(newFilters);
  }

  function onAddFilter() {
    onFilterChange(filters.length, {
      columnId: EMPTY_DEFAULT_COLUMN.id,
      columnValues: [],
    });

    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      }
    }, 0);
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.filtersScrollContainer}
        ref={handleRef}
        onScroll={(e) => {
          setScrollableFadeEffect(e.currentTarget);
        }}
      >
        {filters.map((filter, index) => {
          const column = columns.find((column) => column.id === filter.columnId);
          return (
            <div key={index + (column?.id ?? "")} className={styles.filterItemWrapper}>
              <FilterItem
                columns={supportedColumns}
                column={column}
                values={filter.columnValues}
                onRemove={() => onRemoveFilter(index)}
                onChange={(f) => onFilterChange(index, f)}
              />
            </div>
          );
        })}
      </div>
      <div
        className={classNames(styles.addFilterButton, {
          [styles.filtersAdded]: filters.length > 0,
        })}
        onClick={() => {
          onAddFilter();
          tracking.trackAnalyticsEvent("cim_card_stack_filter_added");
        }}
      >
        <FilterItemPlusIcon color="#1973FF" width={9} height={9} />
        <span className={styles.addFilterButtonText}>Add filter</span>
      </div>
    </div>
  );
}
